import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { winApi } from '@/services/skillKnowledgeApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Eye, Edit, Trash2, Trophy } from 'lucide-react';
import { LoadingState, EmptyState, Pagination } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Win } from '@/types/skillKnowledge';

export const WinListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'skill');
  const canEdit = hasPermission('edit', 'skill');
  const canDelete = hasPermission('delete', 'skill');

  // Build fetch function with filters
  const fetchWinsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      const listParams: Record<string, unknown> = {
        ...params,
        sortBy: 'name',
        sortDirection: 'asc',
      };
      if (filterActive !== 'all') listParams.isActive = filterActive === 'true';
      // Note: searchQuery is not currently supported by the API, but we keep it for future use
      return winApi.list(listParams);
    };
  }, [filterActive]);

  // Use paginated list hook
  const {
    data: wins,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<Win>({
    fetchFn: fetchWinsFn,
    entityName: 'wins',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await winApi.delete(id);
    },
    entityName: 'win',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this win?')) return;
    await deleteMutation.mutate(id);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Win Management</h1>
          <p className="text-muted-foreground mt-2">Manage all wins (achievements/milestones)</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/wins/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Win
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search wins..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9"
              />
            </div>
            <Select value={filterActive} onValueChange={setFilterActive}>
              <SelectTrigger>
                <SelectValue placeholder="Active Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch}>Apply Filters</Button>
          </div>
        </CardContent>
      </Card>

      {/* Wins Table */}
      <Card>
        <CardHeader>
          <CardTitle>Wins ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all wins</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading wins..." />
          ) : (error || wins.length === 0) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load wins. Please try again or create a new one." : "Get started by creating your first win."}
              icon={Trophy}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/wins/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Summary</TableHead>
                    <TableHead>Required Skills</TableHead>
                    <TableHead>Related Pain Points</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wins.map((win) => (
                    <TableRow key={win.id}>
                      <TableCell className="font-medium">{win.name}</TableCell>
                      <TableCell>{win.summary || 'N/A'}</TableCell>
                      <TableCell>{win.requiredSkills?.length || 0}</TableCell>
                      <TableCell>{win.relatedPainPointIds?.length || 0}</TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            win.isActive
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                          }`}
                        >
                          {win.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        {formatDate(win.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/wins/${win.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/wins/${win.id}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => win.id && handleDelete(win.id)}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalElements={pagination.totalElements}
                size={pagination.size}
                onPageChange={setPage}
                onSizeChange={setSize}
                isLoading={isLoading}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WinListPage;
